import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, TrendingUp, TrendingDown, CheckCircle, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { motion } from "framer-motion";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount);
};

const Payments = () => {
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: "",
    type: "receivable",
    amount: "",
    description: "",
    due_date: "",
    paid: false,
    paid_date: "",
    category: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [paymentsRes, customersRes] = await Promise.all([
        axios.get(`${API}/payments`, { withCredentials: true }),
        axios.get(`${API}/customers`, { withCredentials: true })
      ]);
      setPayments(paymentsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount),
        customer_id: formData.customer_id || null
      };
      
      if (editingPayment) {
        await axios.put(`${API}/payments/${editingPayment.id}`, data, { withCredentials: true });
        toast.success("Ödeme güncellendi");
      } else {
        await axios.post(`${API}/payments`, data, { withCredentials: true });
        toast.success("Ödeme eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu ödemeyi silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/payments/${id}`, { withCredentials: true });
      toast.success("Ödeme silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const togglePaid = async (payment) => {
    try {
      const data = {
        ...payment,
        paid: !payment.paid,
        paid_date: !payment.paid ? new Date().toISOString().split('T')[0] : null
      };
      await axios.put(`${API}/payments/${payment.id}`, data, { withCredentials: true });
      toast.success(data.paid ? "Ödeme tamamlandı" : "Ödeme beklemede");
      fetchData();
    } catch (error) {
      toast.error("Güncelleme başarısız");
    }
  };

  const openEditDialog = (payment) => {
    setEditingPayment(payment);
    setFormData({
      customer_id: payment.customer_id || "",
      type: payment.type,
      amount: payment.amount.toString(),
      description: payment.description,
      due_date: payment.due_date || "",
      paid: payment.paid,
      paid_date: payment.paid_date || "",
      category: payment.category || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPayment(null);
    setFormData({
      customer_id: "",
      type: "receivable",
      amount: "",
      description: "",
      due_date: "",
      paid: false,
      paid_date: "",
      category: ""
    });
  };

  const filteredPayments = payments.filter(p => {
    const matchesSearch = p.description?.toLowerCase().includes(search.toLowerCase()) ||
      p.customer_name?.toLowerCase().includes(search.toLowerCase());
    
    if (activeTab === "all") return matchesSearch;
    if (activeTab === "receivable") return matchesSearch && p.type === "receivable";
    if (activeTab === "payable") return matchesSearch && p.type === "payable";
    if (activeTab === "pending") return matchesSearch && !p.paid;
    if (activeTab === "completed") return matchesSearch && p.paid;
    return matchesSearch;
  });

  const totals = {
    receivable: payments.filter(p => p.type === "receivable" && !p.paid).reduce((sum, p) => sum + p.amount, 0),
    payable: payments.filter(p => p.type === "payable" && !p.paid).reduce((sum, p) => sum + p.amount, 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payments-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-outfit font-semibold text-zinc-50">Ödemeler & Tahsilatlar</h1>
          <p className="text-zinc-500 text-sm mt-1">
            Alacak: <span className="text-emerald-400">{formatCurrency(totals.receivable)}</span> | 
            Borç: <span className="text-rose-400">{formatCurrency(totals.payable)}</span>
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
          data-testid="add-payment-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Yeni Kayıt
        </Button>
      </div>

      {/* Tabs and Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <TabsList className="bg-zinc-900 border border-zinc-800">
            <TabsTrigger value="all" className="data-[state=active]:bg-amber-400 data-[state=active]:text-zinc-950">
              Tümü
            </TabsTrigger>
            <TabsTrigger value="receivable" className="data-[state=active]:bg-emerald-400 data-[state=active]:text-zinc-950">
              Alacaklar
            </TabsTrigger>
            <TabsTrigger value="payable" className="data-[state=active]:bg-rose-400 data-[state=active]:text-zinc-950">
              Borçlar
            </TabsTrigger>
            <TabsTrigger value="pending" className="data-[state=active]:bg-amber-400 data-[state=active]:text-zinc-950">
              Bekleyen
            </TabsTrigger>
            <TabsTrigger value="completed" className="data-[state=active]:bg-emerald-400 data-[state=active]:text-zinc-950">
              Tamamlanan
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input 
            placeholder="Ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800"
            data-testid="payment-search"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Tür</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Açıklama</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Müşteri</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Vade</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Tutar</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Durum</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment, index) => (
                <motion.tr 
                  key={payment.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => openEditDialog(payment)}
                  className="border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors cursor-pointer"
                  data-testid={`payment-row-${payment.id}`}
                >
                  <td className="px-4 py-4">
                    <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-sm text-xs ${
                      payment.type === 'receivable' ? 'bg-emerald-400/10 text-emerald-400' : 'bg-rose-400/10 text-rose-400'
                    }`}>
                      {payment.type === 'receivable' ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                      {payment.type === 'receivable' ? 'Alacak' : 'Borç'}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-sm text-zinc-50">{payment.description}</p>
                    {payment.category && (
                      <p className="text-xs text-zinc-500">{payment.category}</p>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm text-zinc-400">
                    {payment.customer_name || "-"}
                  </td>
                  <td className="px-4 py-4 text-sm text-zinc-400">
                    {payment.due_date || "-"}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className={`text-sm font-mono ${
                      payment.type === 'receivable' ? 'text-emerald-400' : 'text-rose-400'
                    }`}>
                      {payment.type === 'receivable' ? '+' : '-'}{formatCurrency(payment.amount)}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <button
                      onClick={(e) => { e.stopPropagation(); togglePaid(payment); }}
                      className={`mx-auto flex items-center gap-1 px-2 py-1 rounded-sm text-xs transition-colors ${
                        payment.paid 
                          ? 'bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20' 
                          : 'bg-amber-400/10 text-amber-400 hover:bg-amber-400/20'
                      }`}
                      data-testid={`toggle-paid-${payment.id}`}
                    >
                      {payment.paid ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                      {payment.paid ? 'Tamamlandı' : 'Bekliyor'}
                    </button>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Pencil className="w-3 h-3 text-zinc-500" />
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleDelete(payment.id); }}
                        className="hover:bg-rose-400/10 text-zinc-400 hover:text-rose-400"
                        data-testid={`delete-payment-${payment.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </motion.tr>
              ))}
              {filteredPayments.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-zinc-500">
                    {search ? "Sonuç bulunamadı" : "Henüz kayıt eklenmemiş"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-outfit">
              {editingPayment ? "Ödeme Düzenle" : "Yeni Ödeme"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Tür *</Label>
                <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="payment-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="receivable">Alacak (Gelir)</SelectItem>
                    <SelectItem value="payable">Borç (Gider)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Tutar (₺) *</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="payment-amount-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Açıklama *</Label>
                <Input 
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="payment-description-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Müşteri</Label>
                <Select value={formData.customer_id || "none"} onValueChange={(v) => setFormData({ ...formData, customer_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="payment-customer-select">
                    <SelectValue placeholder="Seçiniz" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="none">Müşteri yok</SelectItem>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Kategori</Label>
                <Input 
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Örn: Fatura, Hizmet"
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="payment-category-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Vade Tarihi</Label>
                <Input 
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="payment-duedate-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Ödeme Tarihi</Label>
                <Input 
                  type="date"
                  value={formData.paid_date}
                  onChange={(e) => setFormData({ ...formData, paid_date: e.target.value, paid: !!e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="payment-paiddate-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-zinc-700 hover:bg-zinc-800"
              >
                İptal
              </Button>
              <Button 
                type="submit"
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
                data-testid="save-payment-btn"
              >
                {editingPayment ? "Güncelle" : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payments;
